import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { convertToBase64 } from '../../utils/fileUtils';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Textarea } from "../ui/textarea"
import { FileText, X, Search, MessageCircle, ArrowRight, BrainCircuit, UploadCloud } from "lucide-react"
import { userService } from '../../services/userService';

const renderWithBold = (text) => {
  if (!text) return null;
  // Split on ** and alternate between normal and bold
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, idx) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return <strong key={idx}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
};

const TagInput = ({ value, onChange, placeholder, className }) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && inputValue.trim()) {
      e.preventDefault();
      const newTag = inputValue.trim();
      if (newTag && !value.includes(newTag)) {
        onChange([...value, newTag]);
      }
      setInputValue('');
    }
  };

  const handleBlur = () => {
    const newTag = inputValue.trim();
    if (newTag && !value.includes(newTag)) {
      onChange([...value, newTag]);
    }
    setInputValue('');
  };

  const removeTag = (tagToRemove) => {
    onChange(value.filter(tag => tag !== tagToRemove));
  };

  return (
    <div className={`inline-flex items-center flex-wrap gap-1 bg-transparent border-b border-gray-400 border-dashed focus-within:border-solid focus-within:border-black py-1 ${className}`}>
      {value.map(tag => (
        <span key={tag} className="flex items-center bg-gray-200/80 rounded-md px-2 py-0.5 text-base font-normal">
          {tag}
          <button type="button" onClick={() => removeTag(tag)} className="ml-1.5 text-gray-500 hover:text-red-600 focus:outline-none" aria-label={`Remove ${tag}`}>
            <X size={14} />
          </button>
        </span>
      ))}
      <input
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={value.length === 0 ? placeholder : ''}
        className="bg-transparent outline-none px-1 min-w-[120px] flex-grow text-lg placeholder:text-gray-400/80"
      />
    </div>
  );
};

const SearchPage = ({ onContinue }) => {
  const { currentUser } = useAuth();
  const [file, setFile] = useState(null);
  const [base64, setBase64] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [userDetails, setUserDetails] = useState({
    locations: [],
    studies: [],
    hobbies: [],
    universities: [],
    socials: [],
  });
  const [initialAboutResume, setInitialAboutResume] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [parsedJson, setParsedJson] = useState(null);
  const [introAnimation, setIntroAnimation] = useState(false);
  const [pageState, setPageState] = useState('details'); // 'details' or 'search'
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);


  useEffect(() => {
    const fetchAboutResume = async () => {
      if (!currentUser) return;
      setFetching(true);
      try {
        const data = await userService.getAboutResume(currentUser.uid);
        if (data) {
          setInitialAboutResume(data);
          // NOTE: Existing 'about_me' text is not parsed into the new fields to avoid brittle logic.
          if (data.resume && data.filename && !file && !base64) {
            setBase64(data.resume);
            setFile({ name: data.filename });
          }
          if (data.parsed_resume) {
            setParsedJson(data.parsed_resume);
          }
        }
      } catch (err) {
        console.error('Failed to fetch about me/resume:', err);
        setError('Failed to fetch your saved resume/about me.');
      } finally {
        setFetching(false);
      }
    };
    fetchAboutResume();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Hide dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDetailsArrayChange = (fieldName, newValue) => {
    setUserDetails(prev => ({ ...prev, [fieldName]: newValue }));
  };

  useEffect(() => {
    // Wait for 2 seconds before starting the animation
    const timer = setTimeout(() => setIntroAnimation(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  const parseResumeAPI = async (fileToParse, base64ToParse, info = '') => {
    if (!currentUser || !fileToParse || !base64ToParse) return null;

    try {
      const idToken = await currentUser.getIdToken();
      const actualBase64 = base64ToParse.startsWith('data:') ? base64ToParse.split(',')[1] : base64ToParse;

      const response = await fetch(`${process.env.REACT_APP_RESUME_PARSER_URL}/parse-resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ filename: fileToParse.name, pdf_base64: actualBase64, user_info: info }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API request failed with status ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      // Only connectionPoints are omitted; search_recommendations is returned in plaintext
      const connectionPoints = [];
      const searchRecommendations = result.search_recommendations || [];

      return { resume: result.data, connectionPoints, searchRecommendations };
    } catch (err) {
      console.error('Error during resume parsing:', err);
      setError('Failed to parse resume. Please try again.');
      return null;
    }
  };

  const handleFileChange = async (e) => {
    const selected = e.target.files[0];
    if (selected && selected.type === 'application/pdf') {
      setFile(selected);
      setError('');
      setIsParsing(true);
      try {
        const b64 = await convertToBase64(selected);
        setBase64(b64);
        const parsedData = await parseResumeAPI(selected, b64, '');
        if (parsedData) {
          setParsedJson(parsedData);
          setPageState('search'); // Automatically move to search view after parsing
        }
      } catch (error) {
        console.error('Error processing file:', error);
        setError('Failed to process file. Please try again.');
      } finally {
        setIsParsing(false);
      }
    } else {
      alert('Please upload a valid PDF file.');
    }
  };

  const handleContinue = async (isDeepSearch = false) => {
    if (!file || !base64) return;

    if (!currentUser) {
      alert('Please log in to upload your resume.');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { locations, studies, universities, hobbies, socials } = userDetails;
      const parts = [];
      if (locations.length > 0) parts.push(`I come from ${locations.join(', ')}.`);
      if (studies.length > 0 && universities.length > 0) {
        parts.push(`I studied ${studies.join(', ')} at ${universities.join(', ')}.`);
      } else if (studies.length > 0) {
        parts.push(`I studied ${studies.join(', ')}.`);
      } else if (universities.length > 0) {
        parts.push(`I studied at ${universities.join(', ')}.`);
      }

      if (hobbies.length > 0) parts.push(`I enjoy ${hobbies.join(', ')}.`);
      if (socials.length > 0) {
          parts.push(`You can find me online here: ${socials.join(', ')}.`);
      }
      const userInfo = parts.join(' ');

      let finalParsedData = parsedJson;
      const actualBase64 = base64.startsWith('data:') ? base64.split(',')[1] : base64;

      // 1. Check if parsed data exists. It should have been populated on file upload.
      if (!finalParsedData) {
        console.error("No parsed data available. Please re-upload your resume.");
        setError("Parsing information is missing. Please re-upload your resume.");
        setLoading(false);
        return;
      }

      // 2. Check if anything has changed (About Me, file, or parsed data) and save if needed
      const hasChanged =
        !initialAboutResume ||
        userInfo !== (initialAboutResume.about_me || '') ||
        actualBase64 !== (initialAboutResume.resume || '') ||
        file.name !== (initialAboutResume.filename || '') ||
        JSON.stringify(finalParsedData) !== JSON.stringify(initialAboutResume.parsed_resume);

      if (hasChanged) {
        try {
          // Send only clean data without sensitive information
          const cleanParsedData = {
            resume: finalParsedData.resume
          };
          await userService.uploadAboutResume(currentUser.uid, userInfo, actualBase64, file.name, cleanParsedData);
          setInitialAboutResume({ // Update the initial state after a successful save
            about_me: userInfo,
            resume: actualBase64,
            filename: file.name,
            parsed_resume: cleanParsedData,
          });
        } catch (profileErr) {
          console.error('Failed to upload about me and resume to user profile backend:', profileErr);
          setError('Failed to save resume/about me to profile, but proceeding.');
        }
      }

      // 3. Call categorize-connection-points API (only for "Search Now" if something changed)
      let categorizedConnectionPoints = null;
      const connectionPoints = finalParsedData.connectionPoints || [];
      /*
      if (!isDeepSearch && hasChanged && connectionPoints && connectionPoints.length > 0) { // For "Search Now", categorize points.
        try {
          const categorizeResponse = await fetch(`${process.env.REACT_APP_RESUME_PARSER_URL}/categorize-connection-points`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ connection_points: connectionPoints, parsed_resume: finalParsedData.resume }),
          });
          if (categorizeResponse.ok) {
            const categorized = await categorizeResponse.json();
            categorizedConnectionPoints = categorized.categorized_connection_points;
            console.log('Categorized connection points:', categorizedConnectionPoints);
          } else {
            const errorText = await categorizeResponse.text();
            console.error('Categorization API error:', errorText);
          }
        } catch (catErr) {
          console.error('Error calling categorize-connection-points:', catErr);
        }
      } else if (isDeepSearch) {
        // For "Deep Search", pass the raw (uncategorized) connection points to the chatbot.
        // The chatbot component expects this data to initiate a conversational search.
        categorizedConnectionPoints = connectionPoints;
      }
      */

      // 4. Proceed to the next step
      await onContinue({
        name: file.name,
        base64,
        parsed: { ...finalParsedData, userInfo, categorizedConnectionPoints },
        searchQuery,
        isDeepSearch,
      });

    } catch (err) {
      console.error('❌ Error in handleContinue after API call or during parsing:', err);
      // Fallback with dummy data
      const dummyParsed = {
        name: "John Doe",
        contact: {
          email: "john@example.com",
          phone: "(123) 456-7890",
          location: "New York, NY"
        },
        education: {
          "University of Somewhere": {
            degree: "B.Sc. in Computer Science",
            date: "2018 - 2022",
            GPA: "3.9",
            coursework: "Algorithms, Databases, AI"
          }
        },
        experience: [
          {
            company: "TechCorp",
            title: "Software Engineer",
            date: "2022 - Present",
            responsibilities: "- Built full-stack apps\n- Integrated APIs\n- Led projects"
          }
        ],
        skills: ["JavaScript", "React", "Node.js", "Python"],
        certifications: ["AWS Certified Developer"],
        languages: ["English", "Spanish"],
        interests: ["Chess", "Hiking", "Open Source"]
      };

      await onContinue({
        name: file.name,
        base64,
        parsed: { ...dummyParsed, userInfo: '' } // No user info on error
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setBase64('');
  };

  const searchRecommendations = (parsedJson?.searchRecommendations || [])
    .map(rec => (rec && rec.recommendation) || rec)
    .filter(rec => typeof rec === 'string');


  const filteredRecommendations = searchRecommendations && searchQuery
    ? searchRecommendations.filter(r =>
      r.replace(/\*\*([^*]+)\*\*/g, '$1').toLowerCase().includes(searchQuery.toLowerCase())
    )
    : searchRecommendations;

  const handleRecommendationClick = (recommendation) => {
    const cleanRecommendation = recommendation.replace(/\*\*([^*]+)\*\*/g, '$1');
    setSearchQuery(cleanRecommendation);
    setShowDropdown(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };


  const summaryText = [
      userDetails.locations.length > 0 && `From ${userDetails.locations[0]}`,
      userDetails.studies.length > 0 && `Studied ${userDetails.studies[0]}`,
      userDetails.hobbies.length > 0 && `Enjoys ${userDetails.hobbies[0]}`
  ].filter(Boolean).join(' · ') || 'Personal details added';

  return (
    <div className="flex flex-col items-center text-gray-800 p-4 pt-12 pb-12">
      <div className="w-full max-w-3xl flex flex-col items-center text-center">
        {/* Logo and Title - Stays centered initially, then gets pushed up by the content below */}
        <div className={`flex flex-col items-center transition-all duration-1000 ease-out ${introAnimation ? 'scale-100' : 'scale-150'}`}>
          <img src="/logo.png" alt="Quis Logo" className="h-16 w-16 mb-1" />
          <h1 className="text-5xl font-bold tracking-tighter text-gray-900">
            Quis
          </h1>
        </div>

        {/* This wrapper handles the expansion which creates the "slide up" effect for the logo */}
        <div className={`w-full transition-[max-height] duration-1000 ease-out ${introAnimation ? 'max-h-[600px]' : 'max-h-0'} ${showDropdown ? 'overflow-visible' : 'overflow-hidden'}`}>
          {/* This wrapper handles the delayed fade-in of the content */}
          <div className={`w-full transition-opacity duration-700 ease-in ${introAnimation ? 'opacity-100 delay-300' : 'opacity-0'}`}>
            <p className="text-gray-500 mt-2 max-w-md mx-auto">
             AI Powered People Search Engine By Quis Lab
            </p>

            {fetching ? (
              <div className="flex flex-col items-center justify-center min-h-[200px] mt-8">
                <img
                  src="/logo.png"
                  alt="Quis Lab Logo"
                  className="h-16 w-16 mx-auto mb-4 animate-spin-slow"
                  style={{ animation: 'spin 2s linear infinite' }}
                />
                <p className="text-gray-600">Loading your saved info...</p>
              </div>
            ) : (
              <div className="w-full max-w-xl mt-6 mx-auto flex flex-col items-center">
                <AnimatePresence mode="wait">
                  {!isParsing && file && pageState === 'details' && (
                    <motion.div
                      key="details-input"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20, position: 'absolute' }}
                      transition={{ duration: 0.4, ease: "easeInOut" }}
                      className="w-full mb-6"
                    >
                      <div className="mb-4">
                        <h3 className="text-xl font-semibold text-gray-800">Tell us about yourself</h3>
                        <p className="text-sm text-gray-500 mt-1">
                          (Optional) Add your information so that QuisAgent can get personalised conversations starters for a more tailored search.
                        </p>
                      </div>
                      <div className="relative w-full">
                        <div className="text-left text-lg text-gray-700 leading-relaxed p-4 bg-white/20 backdrop-blur-sm rounded-2xl shadow-inner border border-gray-200/50 w-full pr-20 space-y-2">
                          <div>
                            I come from&nbsp;
                            <TagInput
                              value={userDetails.locations}
                              onChange={(newValue) => handleDetailsArrayChange('locations', newValue)}
                              placeholder="e.g., San Francisco"
                            />.
                          </div>
                          <div>
                            I studied&nbsp;
                            <TagInput
                              value={userDetails.studies}
                              onChange={(newValue) => handleDetailsArrayChange('studies', newValue)}
                              placeholder="e.g., Computer Science"
                            />
                            &nbsp;at&nbsp;
                            <TagInput
                              value={userDetails.universities}
                              onChange={(newValue) => handleDetailsArrayChange('universities', newValue)}
                              placeholder="e.g., Stanford University"
                            />.
                          </div>
                          <div>
                            I enjoy&nbsp;
                            <TagInput
                              value={userDetails.hobbies}
                              onChange={(newValue) => handleDetailsArrayChange('hobbies', newValue)}
                              placeholder="e.g., hiking, photography"
                            />.
                          </div>
                          <div>
                            You can find me online at:&nbsp;
                            <TagInput
                              value={userDetails.socials}
                              onChange={(newValue) => handleDetailsArrayChange('socials', newValue)}
                              placeholder="LinkedIn/Portfolio URL"
                              className="w-full mt-1"
                            />
                          </div>
                        </div>
                        <button
                          onClick={() => setPageState('search')}
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black text-white h-12 w-12 rounded-full flex items-center justify-center shadow-md transition hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label="Continue to search"
                        >
                          <ArrowRight className="h-6 w-6" />
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {!isParsing && file && pageState === 'search' && (
                    <motion.div
                      key="search-input"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20, position: 'absolute' }}
                      transition={{ duration: 0.4, ease: "easeInOut" }}
                      className="w-full mb-6 flex items-center gap-3"
                    >
                      <div className="relative flex-grow">
                        <input
                          ref={inputRef}
                          type="text"
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setShowDropdown(true);
                          }}
                          onFocus={() => {
                            if (searchRecommendations && searchRecommendations.length > 0) setShowDropdown(true);
                          }}
                          placeholder="e.g., 'Software engineers in SF with fintech experience'"
                          className={`w-full p-4 pr-16 border-gray-300/50 border bg-white/30 backdrop-blur-sm shadow-sm focus:ring-2 focus:ring-black focus:border-black h-14 text-lg placeholder:text-gray-500 transition-all ${
                            showDropdown && filteredRecommendations.length > 0 ? 'rounded-t-2xl rounded-b-none' : 'rounded-2xl'
                          }`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !loading && searchQuery.trim()) {
                              handleContinue(false); // Default to normal search on Enter
                            }
                          }}
                          autoComplete="off"
                        />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center">
                          <button
                            onClick={() => handleContinue(false)}
                            disabled={loading || !file || !searchQuery.trim()}
                            className="bg-gray-200 text-black h-10 w-10 rounded-full flex items-center justify-center shadow-md transition hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Search Now"
                            title="Search Now"
                          >
                            {loading ? (
                              <span className="animate-spin h-4 w-4 border-2 border-black border-t-transparent rounded-full"></span>
                            ) : (
                              <ArrowRight className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                        {showDropdown && searchRecommendations.length > 0 && (
                          <motion.div
                            ref={dropdownRef}
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="absolute left-0 right-0 z-20 mt-[-1px] bg-white/90 backdrop-blur-md border border-gray-200/50 border-t-0 rounded-b-2xl shadow-lg max-h-60 overflow-auto"
                            style={{ top: '100%' }}
                          >
                            {filteredRecommendations.length > 0 ? (
                              filteredRecommendations.map((recommendation, index) => (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() => handleRecommendationClick(recommendation)}
                                  className="w-full text-left px-4 py-3 hover:bg-black/5 text-sm text-gray-700 border-b last:border-b-0 border-gray-100/50 focus:bg-black/10 transition"
                                  tabIndex={0}
                                >
                                  {renderWithBold(recommendation)}
                                </button>
                              ))
                            ) : (
                              <div className="px-4 py-3 text-gray-500 text-sm">
                                No matching recommendations.
                              </div>
                            )}
                          </motion.div>
                        )}
                      </div>
                      <button
                        onClick={() => handleContinue(true)}
                        disabled={loading || !file || !searchQuery.trim()}
                        className="bg-black text-white h-10 w-10 rounded-full flex items-center justify-center shadow-md transition hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                        aria-label="Deep Search - Let QuisAgent Help You With Your Search Query"
                        title="Deep Search - Let QuisAgent Help You With Your Search Query"
                      >
                        {loading ? (
                          <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                        ) : (
                          <BrainCircuit className="h-4 w-4" />
                        )}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
                {/* Action Buttons */}
                <motion.div layout className="flex items-center justify-center gap-4 mt-2">
                  {!file ? (
                    <label htmlFor="resume-upload" className="cursor-pointer bg-white/40 backdrop-blur-sm border border-gray-300/50 hover:bg-white/60 text-gray-800 font-medium py-2 px-5 rounded-full shadow-sm transition-colors flex items-center gap-2">
                      <UploadCloud className="h-5 w-5" />
                      Attach Resume
                    </label>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 bg-white/40 backdrop-blur-sm border border-green-500/50 text-gray-800 font-medium py-2 px-4 rounded-full shadow-sm transition-colors">
                        <FileText className="h-5 w-5 text-green-600" />
                        <span className="truncate max-w-[150px]">{file.name}</span>
                        {isParsing ? (
                          <span className="animate-spin h-4 w-4 border-2 border-grtay-500 border-t-transparent rounded-full ml-2"></span>
                        ) : (
                          <button onClick={handleRemoveFile} className="text-gray-500 hover:text-red-600 ml-1 transition-colors" aria-label="Remove file">
                            <X className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                      <AnimatePresence>
                        {pageState === 'search' && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            className="flex items-center gap-2 bg-white/40 backdrop-blur-sm border border-gray-300/50 text-gray-800 font-medium py-2 px-4 rounded-full shadow-sm"
                          >
                            <span className="truncate max-w-[250px]">{summaryText}</span>
                            <button
                              onClick={() => setPageState('details')}
                              className="text-gray-500 hover:text-black -mr-2 transition-colors"
                              aria-label="Edit personal details"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  )}
                </motion.div>
                <input
                  id="resume-upload"
                  type="file"
                  className="hidden"
                  accept=".pdf"
                  onChange={handleFileChange}
                />
                {error && !fetching && (
                  <div className="mt-6 text-red-600 text-sm text-center">{error}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
